import { v4 } from "uuid";

import {
  RuleSet,
  Operator,
  FieldName,
  TimeFrame,
  RuleSetDecision,
  Rule,
  GroupType,
  FieldType,
  GroupRole,
  BankruptcyStatus,
  AccountType
} from "./types";
import Equal from "./assets/equals.svg?react";
import NotEqual from "./assets/not-equals.svg?react";
import Chevron from "./assets/chevron.svg?react";
import { GroupTypeLabel } from "../components/GroupTypeLabel";
import {
  readableFieldName,
  readableFieldSubtitle
} from "shared/app/ExplainRecommendationModal/constants";

const fieldTypeTimeFrameOptions = [
  {
    label: "All",
    value: TimeFrame.All
  },
  {
    label: "in 1 year",
    value: TimeFrame.Within1Year
  },
  {
    label: "in 2 years",
    value: TimeFrame.Within2Years
  },
  {
    label: "in 3 years",
    value: TimeFrame.Within3Years
  },
  {
    label: "in 4 years",
    value: TimeFrame.Within4Years
  },
  {
    label: "in 5 years",
    value: TimeFrame.Within5Years
  },
  {
    label: "in 6 years",
    value: TimeFrame.Within6Years
  },
  {
    label: "in 7 years",
    value: TimeFrame.Within7Years
  },
  {
    label: "in 8 years",
    value: TimeFrame.Within8Years
  },
  {
    label: "in 9 years",
    value: TimeFrame.Within9Years
  },
  {
    label: "in 10 years",
    value: TimeFrame.Within10Years
  },
  {
    label: "in 11 years",
    value: TimeFrame.Within11Years
  },
  {
    label: "in 12 years",
    value: TimeFrame.Within12Years
  },
  {
    label: "in 13 years",
    value: TimeFrame.Within13Years
  },
  {
    label: "in 14 years",
    value: TimeFrame.Within14Years
  },
  {
    label: "in 15 years",
    value: TimeFrame.Within15Years
  }
];

export const GROUP_ROLE_LABELS: Record<GroupRole, string> = {
  [GroupRole.All]: "All roles",
  [GroupRole.Applicant]: "Applicant",
  [GroupRole.CoSigner]: "Guarantor",
  [GroupRole.Occupant]: "Occupant"
};

export const fieldTypeOperatorOptions = [
  {
    label: (
      <div className="flex gap-2 items-center">
        <Chevron className="h-4 w-4 shrink-0 stroke-inherit" />
      </div>
    ),
    value: Operator.GreaterThan
  },
  {
    label: (
      <div className="flex gap-2 items-center">
        <Chevron className="h-4 w-4 shrink-0 stroke-inherit transform rotate-180" />
      </div>
    ),
    value: Operator.LessThan
  },
  {
    label: (
      <div className="flex gap-2 items-center stroke-inherit text-inherit">
        <Equal className="h-4 w-4 shrink-0 stroke-inherit text-inherit" />
      </div>
    ),
    value: Operator.Equal
  },
  {
    label: (
      <div className="flex gap-2 items-center">
        <NotEqual className="h-4 w-4 shrink-0 stroke-inherit" />
      </div>
    ),
    value: Operator.NotEqual
  }
];

export const fieldNameOptions = [
  {
    label: "Personal details",
    options: [
      {
        label: <GroupTypeLabel title={readableFieldName[FieldName.Age]} />,
        value: FieldName.Age
      }
    ]
  },
  {
    label: "Applicant roles",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.ApplicationGroupRolesCount]}
          />
        ),
        value: FieldName.ApplicationGroupRolesCount
      }
    ]
  },
  {
    label: "Self-reported income",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={
              readableFieldName[FieldName.SelfReportedGrossIncomeToRentRatio]
            }
            subtitle={
              readableFieldSubtitle[
                FieldName.SelfReportedGrossIncomeToRentRatio
              ]
            }
            group
          />
        ),
        value: FieldName.SelfReportedGrossIncomeToRentRatio
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.SelfReportedGrossIncome]}
            subtitle={readableFieldSubtitle[FieldName.SelfReportedGrossIncome]}
            group
          />
        ),
        value: FieldName.SelfReportedGrossIncome
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.DebtToSelfReportedGrossIncome]}
            subtitle={
              readableFieldSubtitle[FieldName.DebtToSelfReportedGrossIncome]
            }
            group
          />
        ),
        value: FieldName.DebtToSelfReportedGrossIncome
      }
    ]
  },
  {
    label: "Verified income",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.GrossIncomeToRentRatio]}
            subtitle={readableFieldSubtitle[FieldName.GrossIncomeToRentRatio]}
            group
          />
        ),
        value: FieldName.GrossIncomeToRentRatio
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.GrossIncome]}
            subtitle={readableFieldSubtitle[FieldName.GrossIncome]}
            group
          />
        ),
        value: FieldName.GrossIncome
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.NetIncome]}
            subtitle={readableFieldSubtitle[FieldName.NetIncome]}
            group
          />
        ),
        value: FieldName.NetIncome
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.DebtToGrossIncome]}
            subtitle={readableFieldSubtitle[FieldName.DebtToGrossIncome]}
            group
          />
        ),
        value: FieldName.DebtToGrossIncome
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.DebtToNetIncome]}
            subtitle={readableFieldSubtitle[FieldName.DebtToNetIncome]}
            group
          />
        ),
        value: FieldName.DebtToNetIncome
      }
    ]
  },
  {
    label: "Credit score",
    options: [
      {
        label: (
          <GroupTypeLabel title={readableFieldName[FieldName.NoCreditScore]} />
        ),
        value: FieldName.NoCreditScore
      },
      {
        label: (
          <GroupTypeLabel title={readableFieldName[FieldName.CreditScore]} />
        ),
        value: FieldName.CreditScore
      },
      {
        label: (
          <GroupTypeLabel title={readableFieldName[FieldName.PastDueAccount]} />
        ),
        value: FieldName.PastDueAccount
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.UnpaidCollectionsBalance]}
          />
        ),
        value: FieldName.UnpaidCollectionsBalance
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.CollectionsBalance]}
          />
        ),
        value: FieldName.CollectionsBalance
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.BankruptcyRecords]}
          />
        ),
        value: FieldName.BankruptcyRecords
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.ForeclosureAccounts]}
          />
        ),
        value: FieldName.ForeclosureAccounts
      }
    ]
  },
  {
    label: "Housing court",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.HousingCourtRecords]}
          />
        ),
        value: FieldName.HousingCourtRecords
      }
    ]
  },
  {
    label: "Criminal history",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.SexOffenderCheck]}
          />
        ),
        value: FieldName.SexOffenderCheck
      },
      {
        label: (
          <GroupTypeLabel title={readableFieldName[FieldName.OfacRecords]} />
        ),
        value: FieldName.OfacRecords
      },
      {
        label: (
          <GroupTypeLabel title={readableFieldName[FieldName.FelonyRecords]} />
        ),
        value: FieldName.FelonyRecords
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.MisdemeanorRecords]}
          />
        ),
        value: FieldName.MisdemeanorRecords
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.OtherCriminalRecords]}
          />
        ),
        value: FieldName.OtherCriminalRecords
      },
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.AllCriminalRecords]}
          />
        ),
        value: FieldName.AllCriminalRecords
      }
    ]
  },
  {
    label: "Fraud",
    options: [
      {
        label: (
          <GroupTypeLabel
            title={readableFieldName[FieldName.IncomeDocumentFraudCheck]}
            group
          />
        ),
        value: FieldName.IncomeDocumentFraudCheck
      }
    ]
  }
];

// likely redundant
export const fieldOperators = {
  [FieldName.Age]: fieldTypeOperatorOptions,
  [FieldName.HousingCourtRecords]: fieldTypeOperatorOptions,
  [FieldName.BankruptcyRecords]: fieldTypeOperatorOptions,
  [FieldName.SexOffenderCheck]: fieldTypeOperatorOptions,
  [FieldName.PastDueAccount]: fieldTypeOperatorOptions,
  [FieldName.PastDueRentalAccount]: fieldTypeOperatorOptions,
  [FieldName.ForeclosureAccounts]: fieldTypeOperatorOptions,
  [FieldName.UnpaidCollectionsBalance]: fieldTypeOperatorOptions,
  [FieldName.UnpaidCollectionsBalanceRental]: fieldTypeOperatorOptions,
  [FieldName.CreditScore]: fieldTypeOperatorOptions,
  [FieldName.SelfReportedGrossIncome]: fieldTypeOperatorOptions,
  [FieldName.SelfReportedGrossIncomeToRentRatio]: fieldTypeOperatorOptions,
  [FieldName.DebtToSelfReportedGrossIncome]: fieldTypeOperatorOptions,
  [FieldName.DebtToGrossIncome]: fieldTypeOperatorOptions,
  [FieldName.NetIncome]: fieldTypeOperatorOptions,
  [FieldName.GrossIncomeToRentRatio]: fieldTypeOperatorOptions,
  [FieldName.GrossIncome]: fieldTypeOperatorOptions,
  [FieldName.CollectionsBalance]: fieldTypeOperatorOptions,
  [FieldName.CollectionsBalanceRental]: fieldTypeOperatorOptions,
  [FieldName.DebtToNetIncome]: fieldTypeOperatorOptions,
  [FieldName.FelonyRecords]: fieldTypeOperatorOptions,
  [FieldName.MisdemeanorRecords]: fieldTypeOperatorOptions,
  [FieldName.OfacRecords]: fieldTypeOperatorOptions,
  [FieldName.OtherCriminalRecords]: fieldTypeOperatorOptions,
  [FieldName.AllCriminalRecords]: fieldTypeOperatorOptions,
  [FieldName.IncomeDocumentFraudCheck]: fieldTypeOperatorOptions,
  [FieldName.ApplicationGroupRolesCount]: fieldTypeOperatorOptions
};

const fieldTypePercentage = {
  label: "Percentage",
  value: FieldType.Percentage
};
const fieldTypeNumeric = { label: "Numbers", value: FieldType.Numeric };

export const fieldTypeOptions = {
  [FieldName.Age]: [],
  [FieldName.GrossIncome]: [],
  [FieldName.SelfReportedGrossIncome]: [],
  [FieldName.SelfReportedGrossIncomeToRentRatio]: [],
  [FieldName.GrossIncomeToRentRatio]: [],
  [FieldName.NetIncome]: [],
  [FieldName.DebtToSelfReportedGrossIncome]: [fieldTypePercentage],
  [FieldName.DebtToGrossIncome]: [fieldTypePercentage],
  [FieldName.DebtToNetIncome]: [fieldTypePercentage],
  [FieldName.UnpaidCollectionsBalance]: [],
  [FieldName.UnpaidCollectionsBalanceRental]: [],
  [FieldName.CollectionsBalance]: [],
  [FieldName.CollectionsBalanceRental]: [],
  [FieldName.BankruptcyRecords]: fieldTypeTimeFrameOptions,
  [FieldName.HousingCourtRecords]: fieldTypeTimeFrameOptions,
  [FieldName.PastDueAccount]: [fieldTypePercentage, fieldTypeNumeric],
  [FieldName.PastDueRentalAccount]: [fieldTypePercentage, fieldTypeNumeric],
  [FieldName.ForeclosureAccounts]: [fieldTypeNumeric],
  [FieldName.CreditScore]: [],
  [FieldName.NoCreditScore]: [],
  [FieldName.SexOffenderCheck]: [],
  [FieldName.FelonyRecords]: fieldTypeTimeFrameOptions,
  [FieldName.MisdemeanorRecords]: fieldTypeTimeFrameOptions,
  [FieldName.OfacRecords]: fieldTypeTimeFrameOptions,
  [FieldName.OtherCriminalRecords]: fieldTypeTimeFrameOptions,
  [FieldName.AllCriminalRecords]: fieldTypeTimeFrameOptions,
  [FieldName.IncomeDocumentFraudCheck]: [],
  [FieldName.ApplicationGroupRolesCount]: []
};

const groupTypeAverage = { label: "Group average", value: GroupType.Average };
const groupTypeCombined = {
  label: "Group combined",
  value: GroupType.Combined
};
const groupTypeAnyone = { label: "Anyone in group", value: GroupType.Anyone };
const groupTypeEveryone = {
  label: "Everyone in group",
  value: GroupType.Everyone
};

export const groupTypeOptions = {
  [FieldName.Age]: [groupTypeAnyone, groupTypeEveryone, groupTypeAverage],
  [FieldName.SelfReportedGrossIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.SelfReportedGrossIncomeToRentRatio]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.DebtToSelfReportedGrossIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.GrossIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.GrossIncomeToRentRatio]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.NetIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.DebtToGrossIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.DebtToNetIncome]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.UnpaidCollectionsBalance]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.UnpaidCollectionsBalanceRental]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.CollectionsBalance]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.CollectionsBalanceRental]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage,
    groupTypeCombined
  ],
  [FieldName.BankruptcyRecords]: [groupTypeAnyone],
  [FieldName.HousingCourtRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.PastDueAccount]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.PastDueRentalAccount]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.ForeclosureAccounts]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeCombined
  ],
  [FieldName.CreditScore]: [
    groupTypeAnyone,
    groupTypeEveryone,
    groupTypeAverage
  ],
  [FieldName.NoCreditScore]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.SexOffenderCheck]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.FelonyRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.MisdemeanorRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.OfacRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.OtherCriminalRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.AllCriminalRecords]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.IncomeDocumentFraudCheck]: [groupTypeAnyone, groupTypeEveryone],
  [FieldName.ApplicationGroupRolesCount]: [groupTypeCombined]
};

export const fieldAccountTypeMap: Partial<
  Record<FieldName, Partial<Record<string, FieldName>>>
> = {
  [FieldName.PastDueAccount]: {
    rental: FieldName.PastDueRentalAccount
  },
  [FieldName.UnpaidCollectionsBalance]: {
    rental: FieldName.UnpaidCollectionsBalanceRental
  },
  [FieldName.CollectionsBalance]: {
    rental: FieldName.CollectionsBalanceRental
  }
};

export const fieldToBase: Partial<Record<FieldName, FieldName>> = {
  [FieldName.PastDueRentalAccount]: FieldName.PastDueAccount,
  [FieldName.UnpaidCollectionsBalanceRental]:
    FieldName.UnpaidCollectionsBalance,
  [FieldName.CollectionsBalanceRental]: FieldName.CollectionsBalance
};

const accountTypeAll = { label: "All accounts", value: AccountType.All };
const accountTypeRental = {
  label: "Rental account",
  value: AccountType.Rental
};

export const accountTypeOptions: Partial<
  Record<FieldName, { label: string; value: string }[]>
> = {
  [FieldName.PastDueAccount]: [accountTypeAll, accountTypeRental],
  [FieldName.UnpaidCollectionsBalance]: [accountTypeAll, accountTypeRental],
  [FieldName.CollectionsBalance]: [accountTypeAll, accountTypeRental]
};

export const fieldsWithAccountType = [
  FieldName.PastDueAccount,
  FieldName.UnpaidCollectionsBalance,
  FieldName.CollectionsBalance
];

export const defaultOperator = {
  [FieldName.Age]: Operator.LessThan,
  [FieldName.SelfReportedGrossIncomeToRentRatio]: Operator.GreaterThan,
  [FieldName.GrossIncomeToRentRatio]: Operator.GreaterThan,
  [FieldName.DebtToSelfReportedGrossIncome]: Operator.GreaterThan,
  [FieldName.DebtToGrossIncome]: Operator.GreaterThan,
  [FieldName.DebtToNetIncome]: Operator.GreaterThan,
  [FieldName.NetIncome]: Operator.GreaterThan,
  [FieldName.GrossIncome]: Operator.GreaterThan,
  [FieldName.SelfReportedGrossIncome]: Operator.GreaterThan,
  [FieldName.CreditScore]: Operator.LessThan,
  [FieldName.UnpaidCollectionsBalance]: Operator.GreaterThan,
  [FieldName.UnpaidCollectionsBalanceRental]: Operator.GreaterThan,
  [FieldName.CollectionsBalance]: Operator.GreaterThan,
  [FieldName.CollectionsBalanceRental]: Operator.GreaterThan,
  [FieldName.PastDueAccount]: Operator.GreaterThan,
  [FieldName.PastDueRentalAccount]: Operator.GreaterThan,
  [FieldName.ForeclosureAccounts]: Operator.GreaterThan,
  [FieldName.BankruptcyRecords]: Operator.GreaterThan,
  [FieldName.HousingCourtRecords]: Operator.GreaterThan,
  [FieldName.SexOffenderCheck]: Operator.Equal,
  [FieldName.NoCreditScore]: Operator.Equal,
  [FieldName.FelonyRecords]: Operator.Equal,
  [FieldName.MisdemeanorRecords]: Operator.Equal,
  [FieldName.OfacRecords]: Operator.Equal,
  [FieldName.OtherCriminalRecords]: Operator.Equal,
  [FieldName.AllCriminalRecords]: Operator.Equal,
  [FieldName.IncomeDocumentFraudCheck]: Operator.Equal,
  [FieldName.ApplicationGroupRolesCount]: Operator.GreaterThan
};

export const defaultValues = {
  [FieldName.Age]: "55",
  [FieldName.SelfReportedGrossIncomeToRentRatio]: "2.0",
  [FieldName.GrossIncomeToRentRatio]: "2.0",
  [FieldName.NetIncome]: "2000",
  [FieldName.GrossIncome]: "5000",
  [FieldName.SelfReportedGrossIncome]: "5000",
  [FieldName.CreditScore]: "350",
  [FieldName.UnpaidCollectionsBalance]: "200",
  [FieldName.UnpaidCollectionsBalanceRental]: "200",
  [FieldName.CollectionsBalance]: "200",
  [FieldName.CollectionsBalanceRental]: "200",
  // Must be percentage
  [FieldName.PastDueAccount]: "20",
  [FieldName.PastDueRentalAccount]: "20",
  [FieldName.ForeclosureAccounts]: "1",
  [FieldName.DebtToSelfReportedGrossIncome]: "20",
  [FieldName.DebtToGrossIncome]: "20",
  [FieldName.DebtToNetIncome]: "20",
  [FieldName.BankruptcyRecords]: "2",
  [FieldName.HousingCourtRecords]: "2",
  [FieldName.SexOffenderCheck]: 1,
  [FieldName.NoCreditScore]: 1,
  [FieldName.FelonyRecords]: "1",
  [FieldName.MisdemeanorRecords]: "1",
  [FieldName.OfacRecords]: "1",
  [FieldName.OtherCriminalRecords]: "1",
  [FieldName.AllCriminalRecords]: "1",
  [FieldName.IncomeDocumentFraudCheck]: "1",
  [FieldName.ApplicationGroupRolesCount]: "0"
};

export const defaultGroupTypes = {
  [FieldName.Age]: groupTypeEveryone,
  [FieldName.SelfReportedGrossIncomeToRentRatio]: groupTypeCombined,
  [FieldName.GrossIncomeToRentRatio]: groupTypeCombined,
  [FieldName.NetIncome]: groupTypeCombined,
  [FieldName.GrossIncome]: groupTypeCombined,
  [FieldName.SelfReportedGrossIncome]: groupTypeCombined,
  [FieldName.CreditScore]: groupTypeAverage,
  [FieldName.UnpaidCollectionsBalance]: groupTypeAnyone,
  [FieldName.UnpaidCollectionsBalanceRental]: groupTypeAnyone,
  [FieldName.CollectionsBalance]: groupTypeAnyone,
  [FieldName.CollectionsBalanceRental]: groupTypeAnyone,
  [FieldName.PastDueAccount]: groupTypeAnyone,
  [FieldName.PastDueRentalAccount]: groupTypeAnyone,
  [FieldName.ForeclosureAccounts]: groupTypeAnyone,
  [FieldName.DebtToSelfReportedGrossIncome]: groupTypeCombined,
  [FieldName.DebtToGrossIncome]: groupTypeCombined,
  [FieldName.DebtToNetIncome]: groupTypeCombined,
  [FieldName.BankruptcyRecords]: groupTypeAnyone,
  [FieldName.HousingCourtRecords]: groupTypeAnyone,
  [FieldName.SexOffenderCheck]: groupTypeAnyone,
  [FieldName.NoCreditScore]: groupTypeAnyone,
  [FieldName.FelonyRecords]: groupTypeAnyone,
  [FieldName.MisdemeanorRecords]: groupTypeAnyone,
  [FieldName.OfacRecords]: groupTypeAnyone,
  [FieldName.OtherCriminalRecords]: groupTypeAnyone,
  [FieldName.AllCriminalRecords]: groupTypeAnyone,
  [FieldName.IncomeDocumentFraudCheck]: groupTypeAnyone,
  [FieldName.ApplicationGroupRolesCount]: groupTypeCombined
};

export const defaultRule: Omit<Rule, "id" | "result" | "applicant_values"> = {
  field_name: FieldName.GrossIncomeToRentRatio,
  operator: Operator.GreaterThan,
  group_type: defaultGroupTypes[FieldName.GrossIncomeToRentRatio]
    .value as GroupType,
  value: "2.0",
  group_roles: [GroupRole.All]
};

export const defaultRuleSet: RuleSet[] = [
  {
    id: v4(),
    type: RuleSetDecision.Decline,
    rules: []
  },
  {
    id: v4(),
    type: RuleSetDecision.Approve,
    rules: []
  }
];

export const PERCENTAGE_INPUT_LENGTH = 11;

export const bankruptcyStatusOptions = [
  { label: "All", value: BankruptcyStatus.All },
  { label: "Open", value: BankruptcyStatus.Open }
];

export const drugRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName ? baseFieldName + "_drugs_" : "";

  return {
    [fieldNameWithCategory + "any"]: "All drug crime offenses",
    [fieldNameWithCategory + "possession"]: "Drug possession offenses",
    [fieldNameWithCategory + "distribution"]:
      "Drug sale/distribution/manufacturing offenses"
  };
};

const drugCrimesMenuOptions = (baseFieldName: FieldName) => {
  return {
    id: `not_needed_just_for_key_${baseFieldName}_drugs`,
    label: "Drug crime offenses",
    children: [
      {
        id: `${baseFieldName}_drugs_any`,
        label: "All drug crime offenses"
      },
      {
        id: `${baseFieldName}_drugs_possession`,
        label: "Drug possession offenses"
      },
      {
        id: `${baseFieldName}_drugs_distribution`,
        label: "Drug sale/distribution/manufacturing offenses"
      }
    ]
  };
};

export const harassmentRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_harassment_"
    : "";

  return {
    [fieldNameWithCategory + "any"]: "All harassment offense crimes",
    [fieldNameWithCategory + "harassment"]: "Harassment offenses",
    [fieldNameWithCategory + "stalking"]: "Stalking offenses"
  };
};

const harassmentMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_harassment`,
    label: "Harassment crimes offenses",
    children: [
      {
        id: `${baseFieldName}_harassment_any`,
        label: "All harassment offense crimes"
      },
      {
        id: `${baseFieldName}_harassment_harassment`,
        label: "Harassment offenses"
      },
      {
        id: `${baseFieldName}_harassment_stalking`,
        label: "Stalking offenses"
      }
    ]
  };
};

export const otherRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName ? baseFieldName + "_other_" : "";
  return {
    [fieldNameWithCategory + "any"]: "All other offense crimes",
    [fieldNameWithCategory + "other"]: "Other offenses",
    [fieldNameWithCategory + "license"]: "License offenses",
    [fieldNameWithCategory + "taxes"]: "Taxes offenses",
    [fieldNameWithCategory + "justice"]: "Justice offenses",
    [fieldNameWithCategory + "fugitive"]: "Fugitive offenses",
    [fieldNameWithCategory + "disorder"]: "Disorderly conduct offenses",
    [fieldNameWithCategory + "trespassing"]: "Trespassing offenses",
    [fieldNameWithCategory + "vehicle"]: "Vehicle offenses",
    [fieldNameWithCategory + "dumpingLittering"]: "Dumping/Littering offenses"
  };
};

const otherMenuOptions = (baseFieldName: FieldName, isEnabled?: boolean) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_other`,
    label: "Other crime offenses",
    children: [
      {
        id: `${baseFieldName}_other_any`,
        label: "All other offense crimes"
      },
      {
        id: `${baseFieldName}_other_other`,
        label: "Other offenses"
      },
      {
        id: `${baseFieldName}_other_license`,
        label: "License offenses"
      },
      {
        id: `${baseFieldName}_other_taxes`,
        label: "Taxes offenses"
      },
      {
        id: `${baseFieldName}_other_justice`,
        label: "Justice offenses"
      },
      {
        id: `${baseFieldName}_other_fugitive`,
        label: "Fugitive offenses"
      },
      {
        id: `${baseFieldName}_other_disorder`,
        label: "Disorderly conduct offenses"
      },
      {
        id: `${baseFieldName}_other_trespassing`,
        label: "Trespassing offenses"
      },
      {
        id: `${baseFieldName}_other_vehicle`,
        label: "Vehicle offenses"
      },
      {
        id: `${baseFieldName}_other_dumpingLittering`,
        label: "Dumping/Littering offenses"
      }
    ]
  };
};

export const corporateRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_corporate_"
    : "";

  return {
    [fieldNameWithCategory + "any"]: "All corporate offense crimes",
    [fieldNameWithCategory + "corporate"]: "Corporate crime"
  };
};

const corporateMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_corporate`,
    label: "Corporate crime offenses",
    children: [
      {
        id: `${baseFieldName}_corporate_any`,
        label: "All corporate offense crimes"
      },
      {
        id: `${baseFieldName}_corporate_corporate`,
        label: "Corporate crime"
      }
    ]
  };
};

export const conspiracyOrganizedRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_conspiracyOrganizedCrime_"
    : "";

  return {
    [fieldNameWithCategory + "any"]: "All conspiracy/organized offense crimes",
    [fieldNameWithCategory + "conspiracyOrganizedCrime"]:
      "Conspiracy/Organized crime",
    [fieldNameWithCategory + "moneyLaundering"]: "Money laundering",
    [fieldNameWithCategory + "loanSharking"]: "Loan sharking",
    [fieldNameWithCategory + "racketeering"]: "Racketeering"
  };
};

const conspiracyOrganizedMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_conspiracyOrganizedCrime`,
    label: "Conspiracy/Organized crime offenses",
    children: [
      {
        id: `${baseFieldName}_conspiracyOrganizedCrime_any`,
        label: "All conspiracy/organized offense crimes"
      },
      {
        id: `${baseFieldName}_conspiracyOrganizedCrime_conspiracyOrganizedCrime`,
        label: "Conspiracy/Organized crime"
      },
      {
        id: `${baseFieldName}_conspiracyOrganizedCrime_moneyLaundering`,
        label: "Money laundering"
      },
      {
        id: `${baseFieldName}_conspiracyOrganizedCrime_loanSharking`,
        label: "Loan sharking"
      },
      {
        id: `${baseFieldName}_conspiracyOrganizedCrime_racketeering`,
        label: "Racketeering"
      }
    ]
  };
};

export const societalRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_societal_"
    : "";
  return {
    [fieldNameWithCategory + "any"]: "All societal offense crimes",
    [fieldNameWithCategory + "obscenity"]: "Obscenity offenses",
    [fieldNameWithCategory + "gambling"]: "Gambling offenses",
    [fieldNameWithCategory + "alcohol"]: "Alcohol offenses",
    [fieldNameWithCategory + "underTheInfluence"]:
      "Under the influence offenses",
    [fieldNameWithCategory + "weaponsPossession"]:
      "Weapons possession offenses",
    [fieldNameWithCategory + "family"]: "Family offenses"
  };
};

const societalMenuOptions = (baseFieldName: FieldName, isEnabled?: boolean) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_societal`,
    label: "Societal crime offenses",
    children: [
      {
        id: `${baseFieldName}_societal_any`,
        label: "All societal offense crimes"
      },
      {
        id: `${baseFieldName}_societal_obscenity`,
        label: "Obscenity offenses"
      },
      {
        id: `${baseFieldName}_societal_gambling`,
        label: "Gambling offenses"
      },
      {
        id: `${baseFieldName}_societal_alcohol`,
        label: "Alcohol offenses"
      },
      {
        id: `${baseFieldName}_societal_underTheInfluence`,
        label: "Under the influence offenses"
      },
      {
        id: `${baseFieldName}_societal_weaponsPossession`,
        label: "Weapons possession offenses"
      },
      {
        id: `${baseFieldName}_societal_family`,
        label: "Family offenses"
      }
    ]
  };
};

export const destructionRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_destruction_"
    : "";

  return {
    [fieldNameWithCategory + "any"]: "All destruction offense crimes",
    [fieldNameWithCategory + "arson"]: "Arson offenses",
    [fieldNameWithCategory + "vandalism"]: "Vandalism offenses"
  };
};

const destructionMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_destruction`,
    label: "Destruction crime offenses",
    children: [
      {
        id: `${baseFieldName}_destruction_any`,
        label: "All destruction offense crimes"
      },
      {
        id: `${baseFieldName}_destruction_arson`,
        label: "Arson offenses"
      },
      {
        id: `${baseFieldName}_destruction_vandalism`,
        label: "Vandalism offenses"
      }
    ]
  };
};

export const theftOffenseRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName ? baseFieldName + "_theft_" : "";

  return {
    [fieldNameWithCategory + "any"]: "All theft crime offenses",
    [fieldNameWithCategory + "theft"]: "Theft offenses",
    [fieldNameWithCategory + "burglary"]: "Burglary offenses",
    [fieldNameWithCategory + "extortion"]: "Extortion offenses",
    [fieldNameWithCategory + "fraudForgery"]: "Fraud/Forgery offenses",
    [fieldNameWithCategory + "robbery"]: "Robbery offenses",
    [fieldNameWithCategory + "stolenProperty"]: "Stolen Property offenses"
  };
};

const theftOffenseMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;
  return {
    id: `not_needed_just_for_key_${baseFieldName}_theft`,
    label: "Theft crime offenses",
    children: [
      {
        id: `${baseFieldName}_theft_any`,
        label: "All theft crime offenses"
      },
      {
        id: `${baseFieldName}_theft_theft`,
        label: "Theft offenses"
      },
      {
        id: `${baseFieldName}_theft_burglary`,
        label: "Burglary offenses"
      },
      {
        id: `${baseFieldName}_theft_extortion`,
        label: "Extortion offenses"
      },
      {
        id: `${baseFieldName}_theft_fraudForgery`,
        label: "Fraud/Forgery offenses"
      },
      {
        id: `${baseFieldName}_theft_robbery`,
        label: "Robbery offenses"
      },
      {
        id: `${baseFieldName}_theft_stolenProperty`,
        label: "Stolen Property offenses"
      }
    ]
  };
};

export const sexOffenseRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_sexOffense_"
    : "";
  return {
    [fieldNameWithCategory + "any"]: "All sex crime crimes",
    [fieldNameWithCategory + "rape"]: "Rape offenses",
    [fieldNameWithCategory + "sexualActsWithMinors"]:
      "Sexual acts with minors offenses",
    [fieldNameWithCategory + "molestation"]: "Molestation offenses",
    [fieldNameWithCategory + "exploitation"]: "Exploitation offenses",
    [fieldNameWithCategory + "indecency"]: "Indecency offenses",
    [fieldNameWithCategory + "incest"]: "Incest offenses",
    [fieldNameWithCategory + "sexualAbuse"]: "Sexual Assault/Battery offenses",
    [fieldNameWithCategory + "prostitutionPandering"]:
      "Prostitution/Pandering offenses",
    [fieldNameWithCategory + "indecentExposure"]: "Indecent exposure offenses",
    [fieldNameWithCategory + "pornography"]: "Pornography offenses",
    [fieldNameWithCategory + "uncategorized"]: "Uncategorized sex offenses"
  };
};

const sexOffenseMenuOptions = (baseFieldName: FieldName) => {
  return {
    id: `not_needed_just_for_key_${baseFieldName}_sexOffense`,
    label: "Sex crime offenses",
    children: [
      {
        id: `${baseFieldName}_sexOffense_any`,
        label: "All sex crime crimes"
      },
      {
        id: `${baseFieldName}_sexOffense_rape`,
        label: "Rape offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_sexualActsWithMinors`,
        label: "Sexual acts with minors offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_molestation`,
        label: "Molestation offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_exploitation`,
        label: "Exploitation offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_indecency`,
        label: "Indecency offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_incest`,
        label: "Incest offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_sexualAbuse`,
        label: "Sexual Assault/Battery offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_prostitutionPandering`,
        label: "Prostitution/Pandering offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_indecentExposure`,
        label: "Indecent exposure offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_pornography`,
        label: "Pornography offenses"
      },
      {
        id: `${baseFieldName}_sexOffense_uncategorized`,
        label: "Uncategorized offenses"
      }
    ]
  };
};

export const violentRecords = (baseFieldName?: FieldName) => {
  const fieldNameWithCategory = baseFieldName
    ? baseFieldName + "_violence_"
    : "";

  return {
    [fieldNameWithCategory + "any"]: "All violent crime offenses",
    [fieldNameWithCategory + "violence"]: "Violence offenses",
    [fieldNameWithCategory + "murder"]: "Murder offenses",
    [fieldNameWithCategory + "manslaughter"]: "Manslaughter offenses",
    [fieldNameWithCategory + "assault"]: "Assault offenses",
    [fieldNameWithCategory + "battery"]: "Battery offenses",
    [fieldNameWithCategory + "wounding"]: "Wounding offenses",
    [fieldNameWithCategory + "intimidation"]: "Intimidation offenses",
    [fieldNameWithCategory + "kidnappingImprisonment"]:
      "Kidnap/Imprisonment offenses"
  };
};

const violenceCrimesMenuOptions = (
  baseFieldName: FieldName,
  isEnabled?: boolean
) => {
  if (!isEnabled) return;

  return {
    id: `not_needed_just_for_key_${baseFieldName}_violence`,
    label: "Violent crime offenses",
    children: [
      {
        id: `${baseFieldName}_violence_any`,
        label: "All violent crime offenses"
      },
      {
        id: `${baseFieldName}_violence_violence`,
        label: "Violence offenses"
      },
      { id: `${baseFieldName}_violence_murder`, label: "Murder offenses" },
      {
        id: `${baseFieldName}_violence_manslaughter`,
        label: "Manslaughter offenses"
      },
      { id: `${baseFieldName}_violence_assault`, label: "Assault offenses" },
      {
        id: `${baseFieldName}_violence_battery`,
        label: "Battery offenses"
      },
      {
        id: `${baseFieldName}_violence_wounding`,
        label: "Wounding offenses"
      },
      {
        id: `${baseFieldName}_violence_intimidation`,
        label: "Intimidation offenses"
      },
      {
        id: `${baseFieldName}_violence_kidnappingImprisonment`,
        label: "Kidnap/Imprisonment offenses"
      }
    ]
  };
};

const felonyRecordsSelect = {
  [FieldName.FelonyRecords]: "All felony convictions",
  ...drugRecords(FieldName.FelonyRecords),
  ...violentRecords(FieldName.FelonyRecords),
  ...sexOffenseRecords(FieldName.FelonyRecords),
  ...theftOffenseRecords(FieldName.FelonyRecords),
  ...destructionRecords(FieldName.FelonyRecords),
  ...harassmentRecords(FieldName.FelonyRecords),
  ...societalRecords(FieldName.FelonyRecords),
  ...conspiracyOrganizedRecords(FieldName.FelonyRecords),
  ...corporateRecords(FieldName.FelonyRecords),
  ...otherRecords(FieldName.FelonyRecords)
};

const misdemeanorRecordsSelect = {
  [FieldName.MisdemeanorRecords]: "All misdemeanor offenses",
  ...drugRecords(FieldName.MisdemeanorRecords),
  ...violentRecords(FieldName.MisdemeanorRecords),
  ...sexOffenseRecords(FieldName.MisdemeanorRecords),
  ...theftOffenseRecords(FieldName.MisdemeanorRecords),
  ...destructionRecords(FieldName.MisdemeanorRecords),
  ...harassmentRecords(FieldName.MisdemeanorRecords),
  ...societalRecords(FieldName.MisdemeanorRecords),
  ...conspiracyOrganizedRecords(FieldName.MisdemeanorRecords),
  ...corporateRecords(FieldName.MisdemeanorRecords),
  ...otherRecords(FieldName.MisdemeanorRecords)
};

const otherCriminalRecordsSelect = {
  [FieldName.OtherCriminalRecords]: "All other criminal records",
  ...drugRecords(FieldName.OtherCriminalRecords),
  ...violentRecords(FieldName.OtherCriminalRecords),
  ...sexOffenseRecords(FieldName.OtherCriminalRecords),
  ...theftOffenseRecords(FieldName.OtherCriminalRecords),
  ...destructionRecords(FieldName.OtherCriminalRecords),
  ...harassmentRecords(FieldName.OtherCriminalRecords),
  ...societalRecords(FieldName.OtherCriminalRecords),
  ...conspiracyOrganizedRecords(FieldName.OtherCriminalRecords),
  ...corporateRecords(FieldName.OtherCriminalRecords),
  ...otherRecords(FieldName.OtherCriminalRecords)
};

const allCriminalRecordsSelect = {
  [FieldName.AllCriminalRecords]: "All criminal convictions",
  ...drugRecords(FieldName.AllCriminalRecords),
  ...violentRecords(FieldName.AllCriminalRecords),
  ...sexOffenseRecords(FieldName.AllCriminalRecords),
  ...theftOffenseRecords(FieldName.AllCriminalRecords),
  ...destructionRecords(FieldName.AllCriminalRecords),
  ...harassmentRecords(FieldName.AllCriminalRecords),
  ...societalRecords(FieldName.AllCriminalRecords),
  ...conspiracyOrganizedRecords(FieldName.AllCriminalRecords),
  ...corporateRecords(FieldName.AllCriminalRecords),
  ...otherRecords(FieldName.AllCriminalRecords)
};

export const allSelectOptions = {
  ...felonyRecordsSelect,
  ...misdemeanorRecordsSelect,
  ...otherCriminalRecordsSelect,
  ...allCriminalRecordsSelect
};

export const criminalRecordsMenu = (isCriminalParsing?: boolean) => {
  return {
    [FieldName.FelonyRecords]: [
      {
        id: FieldName.FelonyRecords,
        label: felonyRecordsSelect[FieldName.FelonyRecords]
      },
      violenceCrimesMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      sexOffenseMenuOptions(FieldName.FelonyRecords),
      theftOffenseMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      destructionMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      drugCrimesMenuOptions(FieldName.FelonyRecords),
      harassmentMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      societalMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      conspiracyOrganizedMenuOptions(
        FieldName.FelonyRecords,
        isCriminalParsing
      ),
      corporateMenuOptions(FieldName.FelonyRecords, isCriminalParsing),
      otherMenuOptions(FieldName.FelonyRecords, isCriminalParsing)
    ],
    [FieldName.MisdemeanorRecords]: [
      {
        id: FieldName.MisdemeanorRecords,
        label: misdemeanorRecordsSelect[FieldName.MisdemeanorRecords]
      },
      violenceCrimesMenuOptions(
        FieldName.MisdemeanorRecords,
        isCriminalParsing
      ),
      sexOffenseMenuOptions(FieldName.MisdemeanorRecords),
      theftOffenseMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing),
      destructionMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing),
      drugCrimesMenuOptions(FieldName.MisdemeanorRecords),
      harassmentMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing),
      societalMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing),
      conspiracyOrganizedMenuOptions(
        FieldName.MisdemeanorRecords,
        isCriminalParsing
      ),
      corporateMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing),
      otherMenuOptions(FieldName.MisdemeanorRecords, isCriminalParsing)
    ],
    [FieldName.OtherCriminalRecords]: [
      {
        id: FieldName.OtherCriminalRecords,
        label: otherCriminalRecordsSelect[FieldName.OtherCriminalRecords]
      },
      violenceCrimesMenuOptions(
        FieldName.OtherCriminalRecords,
        isCriminalParsing
      ),
      sexOffenseMenuOptions(FieldName.OtherCriminalRecords),
      theftOffenseMenuOptions(
        FieldName.OtherCriminalRecords,
        isCriminalParsing
      ),
      destructionMenuOptions(FieldName.OtherCriminalRecords, isCriminalParsing),
      drugCrimesMenuOptions(FieldName.OtherCriminalRecords),
      harassmentMenuOptions(FieldName.OtherCriminalRecords, isCriminalParsing),
      societalMenuOptions(FieldName.OtherCriminalRecords, isCriminalParsing),
      conspiracyOrganizedMenuOptions(
        FieldName.OtherCriminalRecords,
        isCriminalParsing
      ),
      corporateMenuOptions(FieldName.OtherCriminalRecords, isCriminalParsing),
      otherMenuOptions(FieldName.OtherCriminalRecords, isCriminalParsing)
    ],
    [FieldName.AllCriminalRecords]: [
      {
        id: FieldName.AllCriminalRecords,
        label: allCriminalRecordsSelect[FieldName.AllCriminalRecords]
      },
      violenceCrimesMenuOptions(
        FieldName.AllCriminalRecords,
        isCriminalParsing
      ),
      sexOffenseMenuOptions(FieldName.AllCriminalRecords),
      theftOffenseMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing),
      destructionMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing),
      drugCrimesMenuOptions(FieldName.AllCriminalRecords),
      harassmentMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing),
      societalMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing),
      conspiracyOrganizedMenuOptions(
        FieldName.AllCriminalRecords,
        isCriminalParsing
      ),
      corporateMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing),
      otherMenuOptions(FieldName.AllCriminalRecords, isCriminalParsing)
    ]
  };
};
